import { CLUB_CAPABILITY, capabilitiesForClub, hasClubCapabilityModel } from "./clubCapabilities";

export const MIN_CLUB_PLAYER_SEARCH = 3;
export const CLUB_PLAYER_SEARCH_DEBOUNCE_MS = 300;

export function clubRequiresPlayerMembership(club) {
  if (club?.isPlanRequired === true) {
    return true;
  }
  if (hasClubCapabilityModel(club)) {
    return capabilitiesForClub(club).includes(CLUB_CAPABILITY.MEMBERSHIP);
  }
  return false;
}

export function filterPlayersWhoBoughtMembership(found = [], subscribers = []) {
  const allowed = new Set(
    clubUsersToPlayerOptions(subscribers).map((player) => String(player.id))
  );
  return clubUsersToPlayerOptions(found).filter((player) => allowed.has(String(player.id)));
}

export async function searchReservationPlayers({
  club,
  query,
  searchClubUsers,
  loadSubscribers,
}) {
  if (!canSearchClubPlayers(query) || !club?.clubId) {
    return [];
  }
  const found = clubUsersToPlayerOptions(await searchClubUsers(club.clubId, query));
  if (!clubRequiresPlayerMembership(club)) {
    return found;
  }
  const subscribers = await loadSubscribers(club.clubId);
  return filterPlayersWhoBoughtMembership(found, subscribers);
}

export function canSearchClubPlayers(text) {
  return String(text || "").trim().length >= MIN_CLUB_PLAYER_SEARCH;
}

export function clubUsersToPlayerOptions(users = []) {
  return (Array.isArray(users) ? users : [])
    .map((user) => {
      const id = user?.id || user?.userId;
      const name = user?.name
        || [user?.userFirstName, user?.userLastName].filter(Boolean).join(" ").trim();
      if (!id || !name) {
        return null;
      }
      return { id, name };
    })
    .filter(Boolean);
}

export function mergePlayerOptions(...lists) {
  const byId = new Map();
  lists.flat().forEach((item) => {
    if (item?.id) {
      byId.set(String(item.id), { id: item.id, name: item.name });
    }
  });
  return [...byId.values()];
}

export function reservationPlayersFromItem(item) {
  const list = Array.isArray(item?.player) ? item.player : [];
  return {
    Player: list.map((val) => val.id).filter(Boolean),
    PlayerList: clubUsersToPlayerOptions(list),
  };
}

export function canRemoveReservationPlayer({ lockSelf, userId, removedId }) {
  if (!lockSelf) {
    return true;
  }
  if (removedId == null || removedId === "") {
    return true;
  }
  return String(removedId) !== String(userId || "");
}

export const RESERVATION_PLAYER_REQUIRED = "Please choose at least one player.";
export const MAX_STAFF_RESERVATION_PLAYERS = 1;
export const MAX_PLAYER_RESERVATION_PLAYERS = 8;
export const STAFF_MAX_PLAYER_WARNING = "At most, you can choose just one player.";
export const PLAYER_MAX_PLAYER_WARNING = "You can choose at most 8 players.";

export function reservationPlayerSelectionCap({ staff, player } = {}) {
  if (staff) {
    return MAX_STAFF_RESERVATION_PLAYERS;
  }
  if (player) {
    return MAX_PLAYER_RESERVATION_PLAYERS;
  }
  return null;
}

export function reservationPlayerOverCapMessage({
  staff,
  player,
  selectedCount = 0,
  includingPendingSelect = false,
} = {}) {
  const cap = reservationPlayerSelectionCap({ staff, player });
  if (cap == null) {
    return "";
  }
  const over = includingPendingSelect ? selectedCount >= cap : selectedCount > cap;
  if (!over) {
    return "";
  }
  return staff ? STAFF_MAX_PLAYER_WARNING : PLAYER_MAX_PLAYER_WARNING;
}

export function showReservationEditorAlert(editorRoot, message = RESERVATION_PLAYER_REQUIRED) {
  const alert = editorRoot?.querySelector?.("#reservation-form-alert");
  if (!alert) {
    return false;
  }
  alert.textContent = message;
  alert.hidden = false;
  return true;
}

export function hideReservationEditorAlert(editorRoot) {
  const alert = editorRoot?.querySelector?.("#reservation-form-alert");
  if (alert) {
    alert.hidden = true;
  }
}
